import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, formatApiError } from '../../lib/api';
import ImageUploader from '../../components/admin/ImageUploader';
import TranslateButton from '../../components/admin/TranslateButton';

const slugify = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const emptyProject = {
  slug: '',
  title: '',
  category: '',
  location: '',
  description: [],
  info: [],
  images: [],
  featured: false,
  order: 0,
  // English
  title_en: '',
  category_en: '',
  location_en: '',
  description_en: [],
};

const ProjectForm = () => {
  const { slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const isNew = !routeSlug;

  const [form, setForm] = useState(emptyProject);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [translatingAll, setTranslatingAll] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${encodeURIComponent(routeSlug)}`);
        setForm({ ...emptyProject, ...data });
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [routeSlug, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (v) => {
    set('title', v);
    if (isNew && !form.slug) set('slug', slugify(v));
  };

  // Translate every FR field to EN in one click
  const translateAll = async () => {
    setTranslatingAll(true);
    setError('');
    try {
      const payload = {
        title: form.title,
        category: form.category,
        location: form.location,
        description: form.description,
        info: form.info,
        images: form.images,
      };
      const { data } = await api.post('/admin/translate', { value: payload, target_lang: 'en' });
      // The backend translation of dict isn't supported — call separately for each field
      void data;
    } catch {
      // Fallback: translate each field independently
    }

    // Sequential field-by-field translation (more robust)
    try {
      const tasks = [
        api.post('/admin/translate', { value: form.title, target_lang: 'en' }).then(r => ['title_en', r.data.value]),
        api.post('/admin/translate', { value: form.category, target_lang: 'en' }).then(r => ['category_en', r.data.value]),
        api.post('/admin/translate', { value: form.location, target_lang: 'en' }).then(r => ['location_en', r.data.value]),
        api.post('/admin/translate', { value: form.description, target_lang: 'en' }).then(r => ['description_en', r.data.value]),
        api.post('/admin/translate', { value: form.info, target_lang: 'en' }).then(r => ['__info_translated', r.data.value]),
        api.post('/admin/translate', { value: form.images, target_lang: 'en' }).then(r => ['__images_translated', r.data.value]),
      ];
      const results = await Promise.all(tasks);
      setForm((f) => {
        const next = { ...f };
        for (const [k, v] of results) {
          if (k === '__info_translated') next.info = v;
          else if (k === '__images_translated') next.images = v;
          else next[k] = v;
        }
        return next;
      });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setTranslatingAll(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
        order: Number(form.order) || 0,
        description: form.description.filter((p) => p && p.trim()),
        description_en: (form.description_en || []).filter((p) => p && p.trim()),
        info: form.info.filter((i) => (i.label || '').trim() || (i.value || '').trim()),
        images: form.images.filter((i) => (i.url || '').trim()),
      };
      if (isNew) {
        await api.post('/admin/projects', payload);
      } else {
        await api.put(`/admin/projects/${encodeURIComponent(routeSlug)}`, payload);
      }
      navigate('/admin/projects');
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="font-light text-gray-500">Chargement...</p>;

  return (
    <form onSubmit={onSubmit} data-testid="admin-project-form" className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-light">{isNew ? 'Nouveau projet' : 'Modifier le projet'}</h1>
        <Link to="/admin/projects" className="text-sm font-light tracking-wider uppercase text-gray-500 hover:text-black">← Retour</Link>
      </div>

      {error && <p className="text-[#d94e47] font-light mb-6" data-testid="project-form-error">{error}</p>}

      <div className="space-y-6 bg-white border border-gray-200 p-8">
        {/* Header bilingual */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4">
          <span className="text-xs font-light tracking-wider uppercase text-gray-500">Contenu</span>
          <button
            type="button"
            onClick={translateAll}
            disabled={translatingAll}
            data-testid="translate-all-button"
            className="text-xs font-light tracking-wider uppercase bg-[#d94e47] text-white px-3 py-2 hover:bg-black transition-colors disabled:opacity-50"
          >
            {translatingAll ? 'Traduction en cours...' : 'Traduire tout en EN (IA)'}
          </button>
        </div>

        <BilingualField labelFr="Titre" labelEn="Title (EN)" required
          valueFr={form.title} onChangeFr={onTitleChange}
          valueEn={form.title_en} onChangeEn={(v) => set('title_en', v)}
          testIdFr="field-title" testIdEn="field-title-en" />

        <Field label="Slug (URL)" required value={form.slug} onChange={(v) => set('slug', v)} testId="field-slug" />

        <BilingualField labelFr="Catégorie" labelEn="Category (EN)"
          valueFr={form.category} onChangeFr={(v) => set('category', v)}
          valueEn={form.category_en} onChangeEn={(v) => set('category_en', v)}
          testIdFr="field-category" testIdEn="field-category-en" />

        <BilingualField labelFr="Lieu" labelEn="Location (EN)"
          valueFr={form.location} onChangeFr={(v) => set('location', v)}
          valueEn={form.location_en} onChangeEn={(v) => set('location_en', v)}
          testIdFr="field-location" testIdEn="field-location-en" />

        <div className="grid grid-cols-2 gap-6">
          <Field label="Ordre d'affichage" type="number" value={form.order}
            onChange={(v) => set('order', v)} testId="field-order" />
          <label className="flex items-end gap-2 pb-2">
            <input type="checkbox" checked={!!form.featured}
              onChange={(e) => set('featured', e.target.checked)} data-testid="field-featured" />
            <span className="text-sm font-light">Projet phare (accueil)</span>
          </label>
        </div>

        {/* Description bilingual paragraphs */}
        <BilingualParagraphs
          labelFr="Description FR (paragraphes)" labelEn="Description EN (paragraphs)"
          itemsFr={form.description} itemsEn={form.description_en}
          onChangeFr={(items) => set('description', items)}
          onChangeEn={(items) => set('description_en', items)}
        />

        {/* Info: label/value with EN */}
        <BilingualInfoEditor items={form.info} onChange={(items) => set('info', items)} />

        {/* Gallery with EN captions */}
        <BilingualImagesEditor images={form.images} onChange={(items) => set('images', items)} />
      </div>

      <div className="mt-8 flex gap-4">
        <button type="submit" disabled={saving}
          className="bg-black text-white px-8 py-3 text-sm font-light tracking-wider uppercase hover:bg-[#d94e47] transition-colors disabled:opacity-50"
          data-testid="project-form-submit">
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <Link to="/admin/projects" className="px-8 py-3 text-sm font-light tracking-wider uppercase text-gray-500 hover:text-black">
          Annuler
        </Link>
      </div>
    </form>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, testId }) => (
  <label className="block">
    <span className="block text-xs font-light tracking-wider uppercase text-gray-500 mb-2">{label}{required ? ' *' : ''}</span>
    <input type={type} required={required} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
      data-testid={testId} />
  </label>
);

const BilingualField = ({ labelFr, labelEn, valueFr, onChangeFr, valueEn, onChangeEn, required, testIdFr, testIdEn }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <Field label={labelFr} value={valueFr} onChange={onChangeFr} required={required} testId={testIdFr} />
    <div className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-light tracking-wider uppercase text-gray-500">{labelEn}</span>
        <TranslateButton value={valueFr} onTranslated={onChangeEn} testId={`${testIdEn}-translate`} />
      </div>
      <input type="text" value={valueEn ?? ''} onChange={(e) => onChangeEn(e.target.value)}
        className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
        data-testid={testIdEn} />
    </div>
  </div>
);

const BilingualParagraphs = ({ labelFr, labelEn, itemsFr, itemsEn, onChangeFr, onChangeEn }) => {
  const max = Math.max(itemsFr.length, itemsEn.length, 0);
  const list = Array.from({ length: max }, (_, i) => i);
  const addFr = () => onChangeFr([...itemsFr, '']);
  const removeFr = (i) => onChangeFr(itemsFr.filter((_, idx) => idx !== i));
  const setFr = (i, v) => { const next = [...itemsFr]; next[i] = v; onChangeFr(next); };
  const setEn = (i, v) => { const next = [...itemsEn]; next[i] = v; onChangeEn(next); };

  return (
    <div data-testid="array-description">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <span className="text-xs font-light tracking-wider uppercase text-gray-500">{labelFr}</span>
        <div className="flex items-center justify-between">
          <span className="text-xs font-light tracking-wider uppercase text-gray-500">{labelEn}</span>
          <TranslateButton value={itemsFr} onTranslated={onChangeEn} testId="description-translate-all" />
        </div>
      </div>
      <div className="space-y-4">
        {list.map((i) => (
          <div key={i} className="border border-gray-200 p-4 bg-[#fafafa]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <textarea rows={3} value={itemsFr[i] || ''} onChange={(e) => setFr(i, e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
              <textarea rows={3} value={itemsEn[i] || ''} onChange={(e) => setEn(i, e.target.value)}
                placeholder="English version (leave empty if not translated)"
                className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
            </div>
            <button type="button" onClick={() => removeFr(i)}
              className="mt-3 text-xs font-light text-[#d94e47] hover:underline">Retirer</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={addFr}
        className="mt-4 text-xs font-light tracking-wider uppercase text-black border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
        data-testid="array-description-add">+ Ajouter un paragraphe</button>
    </div>
  );
};

const BilingualInfoEditor = ({ items, onChange }) => {
  const set = (i, key, v) => { const next = [...items]; next[i] = { ...next[i], [key]: v }; onChange(next); };
  const add = () => onChange([...items, { label: '', value: '', label_en: '', value_en: '' }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div data-testid="array-info">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-light tracking-wider uppercase text-gray-500">Informations (label / valeur, FR + EN)</span>
        <TranslateButton value={items} onTranslated={onChange} testId="info-translate-all" />
      </div>
      <div className="space-y-4">
        {items.map((it, idx) => (
          <div key={idx} className="border border-gray-200 p-4 bg-[#fafafa]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <input type="text" placeholder="Label FR (ex. Surface)" value={it.label || ''}
                  onChange={(e) => set(idx, 'label', e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
                <input type="text" placeholder="Valeur FR (ex. 1200 m²)" value={it.value || ''}
                  onChange={(e) => set(idx, 'value', e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
              </div>
              <div className="space-y-2">
                <input type="text" placeholder="Label EN" value={it.label_en || ''}
                  onChange={(e) => set(idx, 'label_en', e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
                <input type="text" placeholder="Value EN" value={it.value_en || ''}
                  onChange={(e) => set(idx, 'value_en', e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
              </div>
            </div>
            <button type="button" onClick={() => remove(idx)}
              className="mt-3 text-xs font-light text-[#d94e47] hover:underline">Retirer</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-4 text-xs font-light tracking-wider uppercase text-black border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
        data-testid="array-info-add">+ Ajouter une info</button>
    </div>
  );
};

const BilingualImagesEditor = ({ images, onChange }) => {
  const set = (i, key, v) => { const next = [...images]; next[i] = { ...next[i], [key]: v }; onChange(next); };
  const add = () => onChange([...images, { url: '', caption: '', caption_en: '' }]);
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div data-testid="array-images">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-light tracking-wider uppercase text-gray-500">Galerie d'images (légendes FR + EN)</span>
        <TranslateButton value={images} onTranslated={onChange} testId="images-translate-all" />
      </div>
      <div className="space-y-4">
        {images.map((img, idx) => (
          <div key={idx} className="border border-gray-200 p-4 bg-[#fafafa] space-y-3">
            <ImageUploader value={img.url} onUploaded={(url) => set(idx, 'url', url)}
              label={`Image ${idx + 1}`} testId={`gallery-img-${idx}`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="text" placeholder="Légende FR (optionnelle)" value={img.caption || ''}
                onChange={(e) => set(idx, 'caption', e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
              <input type="text" placeholder="Caption EN (optional)" value={img.caption_en || ''}
                onChange={(e) => set(idx, 'caption_en', e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47]" />
            </div>
            <button type="button" onClick={() => remove(idx)}
              className="text-xs font-light text-[#d94e47] hover:underline">Retirer</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-4 text-xs font-light tracking-wider uppercase text-black border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
        data-testid="array-images-add">+ Ajouter une image</button>
    </div>
  );
};

export default ProjectForm;
